import React, { useState } from 'react';
import { StarIcon } from 'lucide-react';
import type { StarRating } from '../../types/review';
import { useLanguage } from '../../contexts/LanguageContext';
import { reviewsConfig } from '../../data/reviews';
import { FormField, inputClasses } from '../ui/FormField';
import { buttonClasses } from '../../utils/buttonStyles';
import { track } from '../../utils/analytics';
type ReviewFormProps = {
  onSubmit: (r: {
    rating: StarRating;
    title: string;
    body: string;
    author: string;
    location?: string;
    purchase?: string;
  }) => void;
  onCancel: () => void;
};
const STAR_LABELS_EN = ['Poor', 'Fair', 'Good', 'Very good', 'Excellent'];
const STAR_LABELS_ES = ['Malo', 'Regular', 'Bueno', 'Muy bueno', 'Excelente'];
export function ReviewForm({
  onSubmit,
  onCancel
}: ReviewFormProps) {
  const {
    lang
  } = useLanguage();
  const es = lang === 'es';
  const [rating, setRating] = useState<StarRating | 0>(0);
  const [hover, setHover] = useState(0);
  const [form, setForm] = useState({
    title: '',
    body: '',
    author: '',
    email: '',
    location: '',
    purchase: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm(f => ({
    ...f,
    [k]: e.target.value
  }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const err: Record<string, string> = {};
    if (!rating) err.rating = es ? 'Elige una calificación.' : 'Choose a star rating.';
    if (form.title.trim().length < 3) err.title = es ? 'Agrega un título.' : 'Add a title.';
    if (form.body.trim().length < 20) err.body = es ? 'Escribe al menos 20 caracteres.' : 'Write at least 20 characters.';
    if (!form.author.trim()) err.author = es ? 'Agrega tu nombre.' : 'Add your name.';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) err.email = es ? 'Correo no válido.' : 'Enter a valid email.';
    setErrors(err);
    if (Object.keys(err).length || !rating) return;
    track('review_submitted', {
      rating
    });
    onSubmit({
      rating,
      title: form.title.trim(),
      body: form.body.trim(),
      author: form.author.trim(),
      location: form.location.trim() || undefined,
      purchase: form.purchase || undefined
    });
  };
  const labels = es ? STAR_LABELS_ES : STAR_LABELS_EN;
  const shown = hover || rating;
  return <form noValidate onSubmit={submit} className="rounded-2xl border mf-hairline bg-navy p-5 md:p-7" data-id="element-634" data-mp="3f9s4sd38wtqo">
      <fieldset data-id="element-635" data-mp="3ajk3ujrmybez">
        <legend className="text-sm font-medium text-white" data-id="element-636" data-mp="3aj1z0yqwqovg">{es ? 'Tu calificación' : 'Your rating'}</legend>
        <div className="mt-2 flex items-center gap-3" data-id="element-637" data-mp="36irw5uyewekt">
          <div role="radiogroup" aria-label={es ? 'Calificación' : 'Rating'} className="flex" onMouseLeave={() => setHover(0)} data-id="element-638" data-mp="36fchgt8nh7jx">
            {[1, 2, 3, 4, 5].map(n => <button key={n} type="button" role="radio" aria-checked={rating === n} aria-label={`${n} — ${labels[n - 1]}`} onClick={() => setRating(n as StarRating)} onMouseEnter={() => setHover(n)} className="flex h-11 w-11 items-center justify-center rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-ice" data-id="element-639" data-mp="2wir7rulor89r">
                <StarIcon className={`h-8 w-8 transition-colors duration-100 ${n <= shown ? 'fill-[#FFC94D] text-[#FFC94D]' : 'text-titanium/40'}`} strokeWidth={1.5} aria-hidden data-id="element-640" data-mp="2wcgwxrpb7df1" />
              </button>)}
          </div>
          {shown > 0 && <span className="text-sm text-titanium" data-id="element-641" data-mp="28mw8wueatpyw">{labels[shown - 1]}</span>}
        </div>
        {errors.rating && <p className="mt-1.5 text-sm text-red-300" data-id="element-642" data-mp="2dh0hfi37q623">{errors.rating}</p>}
      </fieldset>

      <div className="mt-6 grid gap-5 md:grid-cols-2" data-id="element-643" data-mp="1fz9uss7g32xs">
        <div className="md:col-span-2" data-id="element-644" data-mp="1ky3dcz1l9k4t">
          <FormField id="rv-title" label={es ? 'Título' : 'Review title'} error={errors.title} data-id="element-645" data-mp="1kws6urgrdya0">
            <input className={inputClasses} value={form.title} onChange={set('title')} maxLength={80} data-id="element-646" data-mp="3mobynv9ikkjx" />
          </FormField>
        </div>
        <div className="md:col-span-2" data-id="element-647" data-mp="87n016ub8bw">
          <FormField id="rv-body" label={es ? 'Tu reseña' : 'Your review'} error={errors.body} data-id="element-648" data-mp="997ew9qx76a">
            <textarea rows={5} className={inputClasses} value={form.body} onChange={set('body')} maxLength={2000} data-id="element-649" data-mp="3rlu59f026nor" />
          </FormField>
        </div>
        <FormField id="rv-name" label={es ? 'Nombre (se mostrará)' : 'Name (displayed)'} error={errors.author} data-id="element-650" data-mp="9aqks6vrqura">
          <input className={inputClasses} value={form.author} onChange={set('author')} autoComplete="given-name" data-id="element-651" data-mp="j6tp9je3f5he" />
        </FormField>
        <FormField id="rv-email" label={es ? 'Correo (no se muestra)' : 'Email (not displayed)'} error={errors.email} hint={es ? 'Para verificar tu compra.' : 'Used to verify your purchase.'} data-id="element-652" data-mp="e90l0vwcoljr">
          <input type="email" className={inputClasses} value={form.email} onChange={set('email')} autoComplete="email" data-id="element-653" data-mp="2g1oq7ask1j26" />
        </FormField>
        <FormField id="rv-location" label={es ? 'Ciudad, estado (opcional)' : 'City, state (optional)'} data-id="element-654" data-mp="10gnsynv8pmjk">
          <input className={inputClasses} value={form.location} onChange={set('location')} data-id="element-655" data-mp="ls9q4cxtpy30" />
        </FormField>
        <FormField id="rv-purchase" label={es ? '¿Qué compraste? (opcional)' : 'What did you buy? (optional)'} data-id="element-656" data-mp="qp9zawpyzgyc">
          <select className={inputClasses} value={form.purchase} onChange={set('purchase')} data-id="element-657" data-mp="cp8pgrrq8z6q">
            <option value="" data-id="element-658" data-mp="hrhj7qcfc59w">—</option>
            {reviewsConfig.purchaseOptions.map(o => <option key={o} value={o} data-id="element-659" data-mp="2txfsnv6ei5u">
                {o}
              </option>)}
          </select>
        </FormField>
      </div>

      <div className="mt-7 flex flex-col gap-3 sm:flex-row" data-id="element-660" data-mp="1x6nbnb53u1fv">
        <button type="submit" className={buttonClasses('primary')} data-id="element-661" data-mp="1x7gbrt9k5sp1">
          {es ? 'Enviar reseña' : 'Submit review'}
        </button>
        <button type="button" onClick={onCancel} className={buttonClasses('ghost')} data-id="element-662" data-mp="20okumwmwxavo">
          {es ? 'Cancelar' : 'Cancel'}
        </button>
      </div>
      <p className="mt-4 text-xs text-titanium/70" data-id="element-663" data-mp="1vnwspfx5b9mo">
        {es ? 'Las reseñas se publican después de verificar la compra. Publicamos reseñas positivas y negativas.' : 'Reviews are published after purchase verification. We publish positive and negative reviews alike.'}
      </p>
    </form>;
}